import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { usePostRegisterCompanyData, useUploadCompanyImage } from "../../../../api/company";
import { THEME } from "../../../../config/constants";
import { useCompanyContext } from "../../../../context/CompanyContext";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { useCompanyStaticOptions } from "../../shared/hooks/useCompanyStaticOptions";
import { CompanyFormData } from "../../shared/types/companyForm.types";
import {
  CompanyRegisterModalProps,
  CompanyRegisterViewModel
} from "../types/companyRegisterView.types";

/** Legacy default: Euro. Kept as the pre-selected currency value even though it may not match a listed option. */
const DEFAULT_CURRENCY_ID = 78;

const todayAsInputDate = () => new Date().toLocaleDateString("en-CA");

/**
 * Ports the exact submit logic of the legacy `CompanyRegisterModal`
 * (src/screens/company/companyRegister/CompanyRegisterModal.tsx): same
 * payload (`status: ACTIVE`, `isParent: false`, `tenantType: Tenant`,
 * `theme`, trimmed name, chosen country/currency/subscription date), same
 * create-then-upload-logo sequence with the same success / "image upload
 * failed" messages, same `triggerRefreshCompanyList()` nudge to the header.
 * The only deliberate difference: "No Of Users"/"No Of SKUS" start as the
 * `1` the legacy form *displayed* but never actually put in the form state.
 */
export const useCompanyRegisterViewModel = ({
  visibleModal,
  closeModal,
  onSuccess
}: CompanyRegisterModalProps): CompanyRegisterViewModel => {
  const { triggerRefreshCompanyList } = useCompanyContext();
  const { isLoading: isCreating, mutate: createCompany } = usePostRegisterCompanyData();
  const { isLoading: isUploadingLogo, mutate: uploadCompanyImage } = useUploadCompanyImage();
  const options = useCompanyStaticOptions();

  const [imageData, setImageData] = useState<File | null>(null);

  const defaultValues = useMemo<CompanyFormData>(
    () => ({
      name: "",
      address: "",
      companyEmail: "",
      phone: "",
      registrationNumber: "",
      website: "",
      industry: "",
      noOfEmployees: 0,
      noOfUsers: 1,
      noOfProducts: 0,
      noOfSKUS: 1,
      tenantCountry: "",
      zipCode: "",
      currency: String(DEFAULT_CURRENCY_ID),
      dateOfSubscription: todayAsInputDate(),
      status: "ACTIVE"
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleModal]
  );

  const methods = useForm<CompanyFormData>({ mode: "all", defaultValues, values: defaultValues });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    return () => {
      setImageData(null);
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);

  const finish = (message: string) => {
    successMessage(message);
    closeModal();
    onSuccess();
    triggerRefreshCompanyList();
  };

  const onSubmit = (data: CompanyFormData) => {
    const currency =
      options.resolveCurrency(data.currency) ??
      (data.currency === String(DEFAULT_CURRENCY_ID) ? DEFAULT_CURRENCY_ID : data.currency);
    const payload = {
      name: data.name.trim(),
      address: data.address,
      companyEmail: data.companyEmail,
      phone: data.phone,
      registrationNumber: data.registrationNumber,
      website: data.website,
      industry: data.industry,
      noOfEmployees: data.noOfEmployees,
      noOfUsers: data.noOfUsers,
      noOfProducts: data.noOfProducts,
      noOfSKUS: data.noOfSKUS,
      zipCode: data.zipCode,
      status: data.status || "ACTIVE",
      isParent: false,
      tenantCountry: options.resolveCountry(data.tenantCountry),
      tenantType: "Tenant",
      dateOfSubscription:
        data.dateOfSubscription && data.dateOfSubscription !== todayAsInputDate()
          ? new Date(data.dateOfSubscription)
          : new Date(),
      theme: THEME,
      currency
    };

    createCompany(payload, {
      onSuccess: (response: any) => {
        const companyId = response?.data?._id;
        if (imageData && companyId) {
          uploadCompanyImage(
            { company_id: companyId, file: imageData, customFileName: companyId },
            {
              onSuccess: () => finish("Company and image registered successfully"),
              onError: () => finish("Company registered successfully (image upload failed)")
            }
          );
        } else {
          finish("Company registered successfully");
        }
      }
    });
  };

  const onCancel = () => {
    closeModal();
    reset();
  };

  return {
    methods,
    countries: options.countries,
    currencies: options.currencies,
    isCountriesLoading: options.isCountriesLoading,
    isCurrencyLoading: options.isCurrencyLoading,
    onLogoSelect: setImageData,
    isSaving: isCreating || isUploadingLogo,
    onSave: handleSubmit(onSubmit),
    onCancel
  };
};
